import type {ColumnModel} from '../models/column-model'
import {areColumnNamesEqual} from '../validations/validate-column-title'
import {getInitialState} from './initial-state'

export type ColumnWarningKind = 'rename-custom-type-column' | 'column-name-collides-with-issue-field'

/**
 * Creates a set of imported issue field names from columns
 * @param columns All columns in the project
 * @returns Set of issue field names
 */
export function getImportedIssueFields(columns: Array<ColumnModel>): Set<string> {
  const importedIssueFields = new Set<string>()
  for (const col of columns) {
    if (col.isIssueField) {
      importedIssueFields.add(col.name)
    }
  }
  return importedIssueFields
}

/**
 * Returns action that user must take for a column (e.g. column collides with system column)
 * @param column The column to check
 * @param issueFieldNames Set of imported issue field names
 * @returns type of warning for a column, if it exists
 */
export function getColumnWarning(column: ColumnModel, issueFieldNames: Set<string>): ColumnWarningKind | undefined {
  if (!column || column.isIssueField) return undefined

  for (const issueFieldName of issueFieldNames) {
    if (areColumnNamesEqual(column.name, issueFieldName)) {
      return 'column-name-collides-with-issue-field'
    }
  }

  const {isOrganization} = getInitialState()
  if (areColumnNamesEqual(column.name, 'type') && column.userDefined && isOrganization) {
    return 'rename-custom-type-column'
  }

  return undefined
}

/**
 * Returns warnings for multiple columns
 * @param columns The columns to check
 * @param allColumns All columns to check against for collisions
 * @returns Map of column id to warning kind
 */
export function getColumnWarnings(
  columns: Array<ColumnModel>,
  allColumns: Array<ColumnModel>,
): Map<string | number, ColumnWarningKind> {
  const warnings = new Map<string | number, ColumnWarningKind>()
  const issueFieldNames = getImportedIssueFields(allColumns)

  for (const column of columns) {
    const warning = getColumnWarning(column, issueFieldNames)
    if (warning) {
      warnings.set(column.id, warning)
    }
  }

  return warnings
}
